import { EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PlayIcon } from '@heroicons/react/24/solid';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getDraws } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import { AppRoutes, buildRoute } from '@/route/routeConfig';

interface Lottery {
  id: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  totalWinners: number;
  emailTemplate: string;
  termsAndConditions: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

interface Draw {
  id: number;
  name: string;
  lotteryId: number;
  totalWinners: number;
  drawExecuteDate: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
  lottery: Lottery;
}

const DrawsList: React.FC = () => {
  const navigate = useNavigate();
  const [draws, setDraws] = useState<Draw[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDraws = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getDraws();
        const drawsData = response?.data?.data || response?.data || [];
        setDraws(Array.isArray(drawsData) ? drawsData : []);
      } catch (error) {
        console.error('Failed to fetch draws:', error);
        setError('Failed to load draws. Please try again.');
        setDraws([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDraws();
  }, []);

  const handleView = (drawId: number, lotteryId: number) => {
    navigate(buildRoute(AppRoutes.LOTTERY_DRAW_DETAIL, { lotteryId, drawId }));
  };

  const handleDelete = (drawId: number) => {
    if (window.confirm('Are you sure you want to delete this draw?')) {
      alert(`Deleted draw with id: ${drawId}`);
    }
  };

  const handleManage = (drawId: number) => {
    alert(`Manage draw with id: ${drawId}`);
  };

  const handleCreateDraw = () => {
    navigate(buildRoute(AppRoutes.LOTTERY_CREATE_DRAW));
  };

  if (isLoading) {
    return (
      <Layout>
        <LayoutCard>
          <div className='flex justify-center items-center p-8'>
            <div className='text-lg text-gray-600'>Loading draws...</div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <LayoutCard>
          <div className='flex justify-center items-center p-8'>
            <div className='text-lg text-red-600'>{error}</div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  return (
    <Layout>
      <LayoutCard>
        <div className='flex justify-between items-center p-4'>
          <AccentTitle title='Draws' />
          <button onClick={handleCreateDraw} className='btn btn-primary flex items-center gap-2'>
            <PlusIcon className='w-4 h-4' />
            Create Draw
          </button>
        </div>
        <div className='flex flex-col gap-2 p-4 border-t'>
          {draws.length === 0 ? (
            <div className='flex justify-center items-center p-8'>
              <div className='text-lg text-gray-600'>No draws found. Create your first draw!</div>
            </div>
          ) : (
            <div className='mt-4 w-full overflow-x-auto'>
              <table className='w-full min-w-[900px]'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Draw Name</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Draw Date</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Total Winners</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Lottery Name</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Action</th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {draws.map((draw) => (
                    <tr key={draw.id} className='hover:bg-green-50'>
                      <td className='p-4 whitespace-nowrap font-medium text-neutralColor-dark'>{draw.name}</td>
                      <td className='p-4 whitespace-nowrap text-neutralColor-medium'>
                        {draw.drawExecuteDate ? new Date(draw.drawExecuteDate).toLocaleDateString() : 'Not scheduled'}
                      </td>
                      <td className='p-4 whitespace-nowrap text-neutralColor-medium'>{draw.totalWinners}</td>
                      <td className='p-4 whitespace-nowrap text-neutralColor-medium'>{draw.lottery?.name || 'N/A'}</td>
                      <td className='p-4 whitespace-nowrap flex gap-3 items-center'>
                        <button
                          aria-label='View Draw'
                          className='hover:text-primaryColor-normal text-gray-500'
                          onClick={() => handleView(draw.id, draw.lotteryId)}
                        >
                          <EyeIcon className='w-5 h-5' />
                        </button>
                        <button aria-label='Delete Draw' className='hover:text-red-600 text-gray-500' onClick={() => handleDelete(draw.id)}>
                          <TrashIcon className='w-5 h-5' />
                        </button>
                        <button
                          aria-label='Execute Draw'
                          className='hover:text-green-600 text-gray-500'
                          onClick={() => handleManage(draw.id)}
                        >
                          <PlayIcon className='w-5 h-5' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </LayoutCard>
    </Layout>
  );
};

export default DrawsList;
